import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ICellOutput, IQuotationCost, IQuotationXrates, QuotationCellsActions } from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { IQuotationsSuppliersSearch } from 'libraries/service/resources/quotation-quotations/quotation-quotations-resource.service';
import { ICurrencyOption } from "libraries/service/resources/currency-list/currency-list-resource.service";
import { EMoveMode } from "libraries/enums/e-move-mode";
import { ICoordsTable } from "libraries/service/table-keyboard-navigation/table-keyboard-navigation.service";

enum COL_INDEX {
    total = 1,
    currency = 2,
}
@Component({
    selector: 'cost-grid-block-value',
    template: `
        <ng-template #template>
            <cell-price [quotationCurrency]="quotationCurrency"
                        [isKeyboardOpen]="focusCoordsFromKeyboard | isSameCoord: rowIndex:1 "
                        [price]="total"
                        [canHaveFooter]="true"
                        [isEditable]="isEditable"
                        [canShowCurrency]="false"
                        [isSticky]="true"
                        [parity]="parity"
                        [leftPosition]="545"
                        [minWidth]="minWidth"
                        [currencyXRates]="currencyXRates"
                        (onPriceChange)="changeCost($event)"
                        (onFocusField)="emitFocusOnTotal()">
            </cell-price>
            <cost-grid-block-cost-currency [total]="total"
                                           [isEditable]="isEditable"
                                           [isKeyboardOpen]="focusCoordsFromKeyboard | isSameCoord: rowIndex:2 "
                                           [isEmpty]="isEmpty"
                                           (onMoveModeChange)="onMoveModeChange.emit($event)"
                                           [options]="options"
                                           (onCurrencyChange)="changeCurrency($event)"
                                           (onFocusField)="emitFocusOnCurrency()">
            </cost-grid-block-cost-currency>
        </ng-template>
    `,
    styles: [`
        :host {
            display: none
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostGridBlockValueComponent implements OnInit, OnChanges {

    @Input()
    public focusCoordsFromKeyboard: ICoordsTable;
    @Input()
    public rowIndex: number;

    @Input()
    public editPrice: boolean = false;
    @Input()
    public total: IQuotationCost;
    @Input()
    public quotationCurrency: string = "EUR";
    @Input()
    public isEditable: boolean = true;
    @Input()
    public isEmpty: boolean;
    @Input()
    public supplier: IQuotationsSuppliersSearch;
    @Input()
    public defaultCurrencies: ICurrencyOption[];
    @Input()
    public parity: "odd" | "even" = "odd";
    @Input()
    public minWidth: string;
    /** Indicate that Edition is opened from keyboard navigation in parent component. */
    @Input()
    public isKeyboardOpen: boolean;
    @Input()
    public currencyXRates: IQuotationXrates[];

    @Output()
    public readonly onCostChange: EventEmitter<ICellOutput> = new EventEmitter<ICellOutput>();
    @Output()
    public readonly onCurrencyChange: EventEmitter<ICellOutput> = new EventEmitter<ICellOutput>();
    @Output()
    public readonly onMoveModeChange: EventEmitter<EMoveMode> = new EventEmitter<EMoveMode>();
    @Output()
    public readonly onFocusField: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    public readonly emptyAndFocus: EventEmitter<ICoordsTable> = new EventEmitter<ICoordsTable>();

    public options: ICurrencyOption[] = [{value: "USD", label: "$"}];

    @ViewChild('template', {read: TemplateRef, static: true})
    private readonly template: TemplateRef<{}>;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.total && changes.total.currentValue) {
            this.getCurrencyOptions();
        }
        if (changes && changes.defaultCurrencies && changes.defaultCurrencies.currentValue) {
            this.getCurrencyOptions();
        }
    }

    public changeCost(cost: IQuotationCost): void {
        this.onCostChange.emit({
            cost,
            itemType: QuotationCellsActions.updateDetailPrice
        });
    }

    public emitFocusOnTotal(): void {
        this.onFocusField.emit(COL_INDEX.total);
    }

    public emitFocusOnCurrency(): void {
        this.onFocusField.emit(COL_INDEX.currency);
    }

    public changeCurrency(cost: IQuotationCost): void {
        this.onCurrencyChange.emit({
            cost,
            itemType: QuotationCellsActions.updatePriceTotalCurrency
        });
    }

    private getCurrencyOptions(): void {
        if (this.supplier && this.supplier.id && this.supplier.currencies.length > 0) {
            this.options = this.supplier.currencies.map((supplierCurrency: string) => ({
                value: supplierCurrency,
                label: supplierCurrency
            }));
        } else {
            this.options = this.defaultCurrencies.map((defaultCurrency: ICurrencyOption) => ({
                value: defaultCurrency.value,
                label: defaultCurrency.value
            }));
        }
    }
}

